/**
 * Performance Analytics Dashboard
 * Sistema completo de monitoreo de rendimiento y Core Web Vitals
 */

import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { getCLS, getFCP, getFID, getLCP, getTTFB } from "web-vitals";
import { COLORS, FONTS } from "../../styles/variables";

// Hook para métricas de performance
export const usePerformanceMetrics = () => {
  const [metrics, setMetrics] = useState({
    lcp: null,
    fid: null,
    cls: null,
    fcp: null,
    ttfb: null,
    customMetrics: {
      bundleSize: null,
      componentRenderTime: null,
      imageLoadTime: null,
      heroRenderTime: null,
    },
  });

  const [performanceGrade, setPerformanceGrade] = useState("");
  const [recommendations, setRecommendations] = useState([]);

  // Medir métricas personalizadas
  const measureCustomMetrics = useCallback(() => {
    // Tiempo de renderizado del hero
    const heroStartTime = performance.now();
    const heroElement = document.querySelector('[data-testid="hero-section"]');

    if (heroElement) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const heroRenderTime = performance.now() - heroStartTime;
            setMetrics((prev) => ({
              ...prev,
              customMetrics: {
                ...prev.customMetrics,
                heroRenderTime,
              },
            }));
            observer.disconnect();
          }
        });
      });
      observer.observe(heroElement);
    }

    // Tamaño del bundle
    if ("getEntriesByType" in performance) {
      const navigationEntries = performance.getEntriesByType("navigation");
      if (navigationEntries.length > 0) {
        const entry = navigationEntries[0];
        setMetrics((prev) => ({
          ...prev,
          customMetrics: {
            ...prev.customMetrics,
            bundleSize: entry.transferSize,
          },
        }));
      }
    }

    // Tiempo de carga de imágenes
    const imageLoadStart = performance.now();
    document.addEventListener(
      "image-loaded",
      (event) => {
        const imageLoadTime = performance.now() - imageLoadStart;
        setMetrics((prev) => ({
          ...prev,
          customMetrics: {
            ...prev.customMetrics,
            imageLoadTime,
          },
        }));
      },
      { once: true }
    );
  }, []);

  // Configurar métricas de Web Vitals
  useEffect(() => {
    // LCP - Largest Contentful Paint
    getLCP((metric) => {
      setMetrics((prev) => ({ ...prev, lcp: metric }));
      console.log("LCP:", metric);
    });

    // FID - First Input Delay
    getFID((metric) => {
      setMetrics((prev) => ({ ...prev, fid: metric }));
      console.log("FID:", metric);
    });

    // CLS - Cumulative Layout Shift
    getCLS((metric) => {
      setMetrics((prev) => ({ ...prev, cls: metric }));
      console.log("CLS:", metric);
    });

    // FCP - First Contentful Paint
    getFCP((metric) => {
      setMetrics((prev) => ({ ...prev, fcp: metric }));
      console.log("FCP:", metric);
    });

    // TTFB - Time to First Byte
    getTTFB((metric) => {
      setMetrics((prev) => ({ ...prev, ttfb: metric }));
      console.log("TTFB:", metric);
    });

    // Métricas personalizadas
    measureCustomMetrics();

    // Observer para cambios en layout
    const layoutObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.hadRecentInput) continue;
        console.log("Layout Shift:", entry.value);
      }
    });

    if ("observe" in layoutObserver) {
      layoutObserver.observe({ type: "layout-shift", buffered: true });
    }

    return () => {
      if ("disconnect" in layoutObserver) {
        layoutObserver.disconnect();
      }
    };
  }, [measureCustomMetrics]);

  // Calcular grado de performance
  useEffect(() => {
    if (metrics.lcp && metrics.fid && metrics.cls) {
      const grade = calculatePerformanceGrade(metrics);
      const recs = generateRecommendations(metrics);

      setPerformanceGrade(grade);
      setRecommendations(recs);
    }
  }, [metrics]);

  return {
    metrics,
    performanceGrade,
    recommendations,
    refreshMetrics: measureCustomMetrics,
  };
};

// Calcular grado de performance basado en Web Vitals
const calculatePerformanceGrade = (metrics) => {
  let score = 0;
  let count = 0;

  // LCP scoring
  if (metrics.lcp) {
    if (metrics.lcp.value <= 2500) score += 100;
    else if (metrics.lcp.value <= 4000) score += 75;
    else score += 25;
    count++;
  }

  // FID scoring
  if (metrics.fid) {
    if (metrics.fid.value <= 100) score += 100;
    else if (metrics.fid.value <= 300) score += 75;
    else score += 25;
    count++;
  }

  // CLS scoring
  if (metrics.cls) {
    if (metrics.cls.value <= 0.1) score += 100;
    else if (metrics.cls.value <= 0.25) score += 75;
    else score += 25;
    count++;
  }

  const averageScore = count > 0 ? score / count : 0;

  if (averageScore >= 90) return "A";
  if (averageScore >= 75) return "B";
  if (averageScore >= 60) return "C";
  if (averageScore >= 40) return "D";
  return "F";
};

// Generar recomendaciones basadas en métricas
const generateRecommendations = (metrics) => {
  const recommendations = [];

  if (metrics.lcp && metrics.lcp.value > 2500) {
    recommendations.push({
      type: "critical",
      metric: "LCP",
      message: "Largest Contentful Paint es muy lento",
      suggestions: [
        "Optimizar imágenes críticas",
        "Usar preload para recursos importantes",
        "Minimizar CSS blocking",
        "Implementar lazy loading",
      ],
    });
  }

  if (metrics.fid && metrics.fid.value > 100) {
    recommendations.push({
      type: "warning",
      metric: "FID",
      message: "First Input Delay necesita mejoras",
      suggestions: [
        "Reducir JavaScript en el hilo principal",
        "Usar Web Workers para tareas pesadas",
        "Dividir código con code splitting",
        "Optimizar event handlers",
      ],
    });
  }

  if (metrics.cls && metrics.cls.value > 0.1) {
    recommendations.push({
      type: "critical",
      metric: "CLS",
      message: "Cumulative Layout Shift es alto",
      suggestions: [
        "Establecer dimensiones para imágenes",
        "Reservar espacio para contenido dinámico",
        "Evitar insertar contenido arriba del fold",
        "Usar transform en lugar de cambiar layout",
      ],
    });
  }

  if (metrics.customMetrics.bundleSize > 1024 * 1024) {
    // > 1MB
    recommendations.push({
      type: "warning",
      metric: "Bundle Size",
      message: "El tamaño del bundle es grande",
      suggestions: [
        "Implementar code splitting",
        "Eliminar dependencias no utilizadas",
        "Usar dynamic imports",
        "Optimizar imágenes y assets",
      ],
    });
  }

  return recommendations;
};

// Componente Dashboard de Performance
export const PerformanceDashboard = ({ isVisible = false }) => {
  const { metrics, performanceGrade, recommendations } =
    usePerformanceMetrics();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isVisible) {
    return null;
  }

  return (
    <DashboardContainer $isExpanded={isExpanded}>
      <DashboardHeader onClick={() => setIsExpanded(!isExpanded)}>
        <GradeIndicator grade={performanceGrade}>
          {performanceGrade || "?"}
        </GradeIndicator>
        <span>Performance</span>
        <ToggleButton $isExpanded={isExpanded}>▼</ToggleButton>
      </DashboardHeader>

      {isExpanded && (
        <DashboardContent>
          <MetricsGrid>
            <MetricCard>
              <MetricLabel>LCP</MetricLabel>
              <MetricValue $status={getMetricStatus("lcp", metrics.lcp?.value)}>
                {metrics.lcp
                  ? `${Math.round(metrics.lcp.value)}ms`
                  : "Midiendo..."}
              </MetricValue>
            </MetricCard>

            <MetricCard>
              <MetricLabel>FID</MetricLabel>
              <MetricValue $status={getMetricStatus("fid", metrics.fid?.value)}>
                {metrics.fid
                  ? `${Math.round(metrics.fid.value)}ms`
                  : "Esperando..."}
              </MetricValue>
            </MetricCard>

            <MetricCard>
              <MetricLabel>CLS</MetricLabel>
              <MetricValue $status={getMetricStatus("cls", metrics.cls?.value)}>
                {metrics.cls ? metrics.cls.value.toFixed(3) : "Midiendo..."}
              </MetricValue>
            </MetricCard>

            <MetricCard>
              <MetricLabel>FCP</MetricLabel>
              <MetricValue>
                {metrics.fcp
                  ? `${Math.round(metrics.fcp.value)}ms`
                  : "Midiendo..."}
              </MetricValue>
            </MetricCard>

            <MetricCard>
              <MetricLabel>TTFB</MetricLabel>
              <MetricValue>
                {metrics.ttfb
                  ? `${Math.round(metrics.ttfb.value)}ms`
                  : "Midiendo..."}
              </MetricValue>
            </MetricCard>

            <MetricCard>
              <MetricLabel>Bundle</MetricLabel>
              <MetricValue>
                {metrics.customMetrics.bundleSize
                  ? `${Math.round(metrics.customMetrics.bundleSize / 1024)}KB`
                  : "Midiendo..."}
              </MetricValue>
            </MetricCard>
          </MetricsGrid>

          {recommendations.length > 0 && (
            <RecommendationsSection>
              <SectionTitle>Recomendaciones</SectionTitle>
              {recommendations.map((rec, index) => (
                <RecommendationCard key={index} $type={rec.type}>
                  <RecommendationHeader>
                    <MetricBadge>{rec.metric}</MetricBadge>
                    <RecommendationTitle>{rec.message}</RecommendationTitle>
                  </RecommendationHeader>
                  <SuggestionsList>
                    {rec.suggestions.map((suggestion, idx) => (
                      <SuggestionItem key={idx}>• {suggestion}</SuggestionItem>
                    ))}
                  </SuggestionsList>
                </RecommendationCard>
              ))}
            </RecommendationsSection>
          )}
        </DashboardContent>
      )}
    </DashboardContainer>
  );
};

// Determinar estado de métrica
const getMetricStatus = (metric, value) => {
  if (!value) return "unknown";

  const thresholds = {
    lcp: { good: 2500, needs_improvement: 4000 },
    fid: { good: 100, needs_improvement: 300 },
    cls: { good: 0.1, needs_improvement: 0.25 },
  };

  if (value <= thresholds[metric].good) return "good";
  if (value <= thresholds[metric].needs_improvement) return "needs_improvement";
  return "poor";
};

// Styled Components
const DashboardContainer = styled.div`
  position: fixed;
  left: 20px;
  bottom: 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  z-index: 9999;
  min-width: 280px;
  max-width: ${(props) => (props.$isExpanded ? "600px" : "200px")};
  transition: all 0.3s ease;
`;

const DashboardHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  gap: 8px;
  font-weight: ${FONTS.WEIGHT_SEMI_BOLD};
`;

const GradeIndicator = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  color: white;
  background: ${(props) => {
    switch (props.grade) {
      case "A":
        return "#10B981";
      case "B":
        return "#3B82F6";
      case "C":
        return "#F59E0B";
      case "D":
        return "#EF4444";
      case "F":
        return "#DC2626";
      default:
        return "#6B7280";
    }
  }};
`;

const ToggleButton = styled.div`
  margin-left: auto;
  transform: ${(props) =>
    props.$isExpanded ? "rotate(180deg)" : "rotate(0deg)"};
  transition: transform 0.3s ease;
`;

const DashboardContent = styled.div`
  padding: 16px;
  border-top: 1px solid #e5e7eb;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
`;

const MetricCard = styled.div`
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  text-align: center;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  font-weight: ${FONTS.WEIGHT_SEMI_BOLD};
  color: #6b7280;
  margin-bottom: 4px;
`;

const MetricValue = styled.div`
  font-size: 16px;
  font-weight: ${FONTS.WEIGHT_BOLD};
  color: ${(props) => {
    switch (props.$status) {
      case "good":
        return "#10B981";
      case "needs_improvement":
        return "#F59E0B";
      case "poor":
        return "#EF4444";
      default:
        return "#374151";
    }
  }};
`;

const RecommendationsSection = styled.div`
  margin-top: 20px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: ${FONTS.WEIGHT_SEMI_BOLD};
  color: #374151;
`;

const RecommendationCard = styled.div`
  padding: 12px;
  background: ${(props) =>
    props.$type === "critical" ? "#FEF2F2" : "#FEF3C7"};
  border-left: 4px solid
    ${(props) => (props.$type === "critical" ? "#EF4444" : "#F59E0B")};
  border-radius: 6px;
  margin-bottom: 8px;
`;

const RecommendationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const MetricBadge = styled.span`
  background: ${COLORS.BLUE_MAIN};
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
`;

const RecommendationTitle = styled.div`
  font-size: 12px;
  font-weight: ${FONTS.WEIGHT_SEMI_BOLD};
  color: #374151;
`;

const SuggestionsList = styled.div`
  font-size: 11px;
  color: #6b7280;
`;

const SuggestionItem = styled.div`
  margin-bottom: 2px;
`;

export default PerformanceDashboard;
